import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation.js";
import { getFinancialYearLabels } from "../../brsrFinancialYearLabels.js";
import {
  BACK_ROW,
  BODY_FONT,
  HEADER_FONT_11,
  HEADER_ROW,
  VALUE_FILL,
  hideColumnsAfter,
  setCell,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  addSheetTitle,
  addTableSpacer,
  applyBorderPattern,
  displayText,
  formatPercent,
  outerRowPatterns,
  setupPrinciple2DetailSheet,
  writeDataRow,
  hideRowsThrough,
} from "./principle2DetailHelpers.js";

export const SHEET_NAME = "Other retirement benefits";

const WIDTHS = {
  A: 9.1796875,
  B: 9.1796875,
  C: 9.1796875,
  D: 9.1796875,
  E: 31.7265625,
  F: 28.54296875,
  G: 28.54296875,
  H: 28.54296875,
  I: 28.54296875,
  J: 28.54296875,
  K: 28.54296875,
  L: 9.1796875,
  M: 9.1796875,
  N: 9.1796875,
};

const SUB_HEADER_ROW = HEADER_ROW + 1;
const TEMPLATE_ROW = 11;
const SPACER_ROW = 12;
const DATA_START_ROW = 13;

const COL_END = COL_K;

const HEADER_BORDERS = Array(8).fill("tttt");
const TEMPLATE_BORDERS = ["-tt-", "-tt-", "-tt-", "-tt-", "-tt-", "-tt-", "-tt-", "-ttt"];
const DATA_BORDERS = TEMPLATE_BORDERS;

const SUB_HEADERS = [
  "No. of employees covered as a % of total employees",
  "No. of workers covered as a % of total workers",
  "Deducted and deposited with the authority (Y/N/N.A.)",
  "No. of employees covered as a % of total employees",
  "No. of workers covered as a % of total workers",
  "Deducted and deposited with the authority (Y/N/N.A.)",
];

function addPrinciple3BackLink(ws, colEnd) {
  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_3, {
    row: BACK_ROW,
    col: colEnd,
    mergeEnd: colEnd,
    align: "right",
    parentCell: "D8",
  });
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

export function buildOtherRetirementBenefitsSheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet(SHEET_NAME, { views: [{ showGridLines: false }] });
  const fy = getFinancialYearLabels(reportingYear);
  const fyCurrent = fy.currentYear ? `FY (${fy.currentYear})` : "FY (Current Financial Year)";
  const fyPrevious = fy.previousYear ? `PY (${fy.previousYear})` : "PY (Previous Financial Year)";

  setupPrinciple2DetailSheet(ws, {
    widths: WIDTHS,
    colEnd: COL_END,
    ySplit: 12,
    topLeftCell: "D13",
    hiddenCols: ["A", "B"],
  });
  hideColumnsAfter(ws, 14);
  for (let row = 1; row <= 5; row += 1) {
    ws.getRow(row).hidden = true;
  }
  addPrinciple3BackLink(ws, COL_END);
  addSheetTitle(ws, COL_END, "Details of Other Retirement benefits", {
    borderPatterns: outerRowPatterns(COL_D, COL_END, {
      first: "tttt",
      middle: "tttt",
      last: "tttt",
    }),
  });

  applyBorderPattern(ws, HEADER_ROW, COL_D, HEADER_BORDERS);
  setCell(ws, HEADER_ROW, COL_D, "Sr. No.", {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });
  setCell(ws, HEADER_ROW, COL_E, "Name of Benefits", {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });
  ws.mergeCells(HEADER_ROW, COL_F, HEADER_ROW, COL_H);
  setCell(ws, HEADER_ROW, COL_F, fyCurrent, {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });
  ws.mergeCells(HEADER_ROW, COL_I, HEADER_ROW, COL_K);
  setCell(ws, HEADER_ROW, COL_I, fyPrevious, {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });

  applyBorderPattern(ws, SUB_HEADER_ROW, COL_D, HEADER_BORDERS);
  ws.getRow(SUB_HEADER_ROW).height = 37.5;
  setCell(ws, SUB_HEADER_ROW, COL_D, "Sr. No.", {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });
  setCell(ws, SUB_HEADER_ROW, COL_E, "Name of Benefits", {
    font: HEADER_FONT_11,
    bordered: false,
    align: "center",
  });
  SUB_HEADERS.forEach((label, idx) => {
    setCell(ws, SUB_HEADER_ROW, COL_F + idx, label, {
      font: HEADER_FONT_11,
      bordered: false,
      align: "center",
    });
  });
  ws.mergeCells(HEADER_ROW, COL_D, SUB_HEADER_ROW, COL_D);
  ws.mergeCells(HEADER_ROW, COL_E, SUB_HEADER_ROW, COL_E);

  applyBorderPattern(ws, TEMPLATE_ROW, COL_D, TEMPLATE_BORDERS);
  for (let col = COL_D; col <= COL_END; col += 1) {
    setCell(ws, TEMPLATE_ROW, col, "", { bordered: false, font: BODY_FONT });
  }
  ws.getRow(TEMPLATE_ROW).height = 29;
  ws.getRow(TEMPLATE_ROW).hidden = true;

  addTableSpacer(ws, COL_D, COL_END, { row: SPACER_ROW });

  const retirement = sectionC?.principle3?.essentialIndicators?.retirementBenefits || {};
  const current = retirement?.currentFinancialYear?.others || {};
  const previous = retirement?.previousFinancialYear?.others || {};
  const benefitName = safeText(
    current?.headerOthers || previous?.headerOthers,
    "Others - please specify"
  );
  const row = DATA_START_ROW;

  writeDataRow(
    ws,
    row,
    COL_D,
    COL_END,
    [
      {
        col: COL_D,
        value: 1,
        align: "right",
        fill: null,
        font: BODY_FONT,
        vertical: "top",
      },
      { col: COL_E, value: displayText(benefitName), align: "left", fill: VALUE_FILL },
      {
        col: COL_F,
        value: formatPercent(current?.employeesCovered),
        align: "center",
        fill: VALUE_FILL,
      },
      {
        col: COL_G,
        value: formatPercent(current?.workersCovered),
        align: "center",
        fill: VALUE_FILL,
      },
      {
        col: COL_H,
        value: displayText(current?.deductedDeposited),
        align: "center",
        fill: VALUE_FILL,
      },
      {
        col: COL_I,
        value: formatPercent(previous?.employeesCovered),
        align: "center",
        fill: VALUE_FILL,
      },
      {
        col: COL_J,
        value: formatPercent(previous?.workersCovered),
        align: "center",
        fill: VALUE_FILL,
      },
      {
        col: COL_K,
        value: displayText(previous?.deductedDeposited),
        align: "center",
        fill: VALUE_FILL,
      },
    ],
    { patterns: DATA_BORDERS }
  );
  ws.getCell(row, COL_D).alignment = {
    horizontal: "right",
    vertical: "top",
    wrapText: true,
  };

  return ws;
}
